import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { faHourglassHalf, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { PendingInvitationView } from "../utils/pendingInvitation.util";

import type { SenderRecipientOption } from "./SenderRecipientDropdown";

/** The "Invitation pending" tag in the composer header (PREC-4216, mockup A2). */
export const InvitationPendingTag: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<span
			className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-status-warning-light text-tag-warning-dark text-[10px] font-semibold uppercase tracking-wide"
			data-testid="invitation-pending-tag"
		>
			<Icon className="w-3 h-3" icon={faHourglassHalf} />
			{t("inbox.pendingInvitation.tag")}
		</span>
	);
};

interface NoticeChoiceProps {
	checked: boolean;
	label: string;
	hint: string;
	testId: string;
	onSelect?: () => void;
}

/** One choice of the notice: a radio with a label and the consequence under it. */
const NoticeChoice: FunctionComponent<NoticeChoiceProps> = ({
	checked,
	label,
	hint,
	testId,
	onSelect,
}) => (
	<button
		aria-checked={checked}
		className="flex items-start gap-2 text-left cursor-pointer"
		data-testid={testId}
		role="radio"
		type="button"
		onClick={checked ? undefined : onSelect}
	>
		<span
			className={classNames(
				"mt-0.5 w-3.5 h-3.5 shrink-0 rounded-full border bg-white flex items-center justify-center",
				checked ? "border-brand-light" : "border-border"
			)}
		>
			{checked && <span className="w-2 h-2 rounded-full bg-brand-light" />}
		</span>
		<span className="flex flex-col">
			<span className="text-xs text-text">{label}</span>
			<span className="text-[11px] text-text-meta">{hint}</span>
		</span>
	</button>
);

interface PendingInvitationNoticeProps {
	view: PendingInvitationView;
	leadFirstName?: string | null;
	/** The sender's display name ("Me" for the current user, as the sender picker shows it). */
	senderDisplayName?: string;
	/** "Send from <colleague>": the drafts move to that sender and go out as ordinary messages. */
	onSendFromColleague: (option: SenderRecipientOption) => void;
}

/**
 * Explains a lead whose invitation from this sender is still pending, with no chat, and what
 * happens to the message (PREC-4216, mockup A2): "Send when accepted" is the default and the
 * draft's own state — the message is parked behind the invitation — and each colleague who can
 * message the lead now is the alternative. Picking one switches the sender; there is no InMail
 * choice (PREC-4676).
 */
export const PendingInvitationNotice: FunctionComponent<PendingInvitationNoticeProps> = ({
	view,
	leadFirstName,
	senderDisplayName,
	onSendFromColleague,
}) => {
	const { t } = useTranslation();
	const name = leadFirstName || t("inbox.pendingInvitation.theLead");
	const isOwn = !senderDisplayName || senderDisplayName === "Me";
	const title = isOwn
		? t("inbox.pendingInvitation.titleOwn", { name })
		: t("inbox.pendingInvitation.titleColleague", { name, sender: senderDisplayName });
	const waitHint = view.deadline
		? t("inbox.pendingInvitation.sendWhenAcceptedHint", {
				date: format(new Date(view.deadline), "dd.MM.yyyy"),
			})
		: t("inbox.pendingInvitation.sendWhenAcceptedHintNoDeadline");

	return (
		<div
			className="flex flex-col gap-2 px-3 py-2 bg-background-warning border-x border-border-light"
			data-testid="pending-invitation-notice"
		>
			<div className="flex items-start gap-2 text-tag-warning-dark">
				<Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" icon={faTriangleExclamation} />
				<div className="flex flex-col">
					<span className="text-xs font-semibold">{title}</span>
					<span className="text-xs">{t("inbox.pendingInvitation.body")}</span>
				</div>
			</div>
			<div
				aria-label={t("inbox.pendingInvitation.tag")}
				className="flex flex-col gap-1.5 pl-5"
				role="radiogroup"
			>
				<NoticeChoice
					checked={true}
					hint={waitHint}
					label={t("inbox.pendingInvitation.sendWhenAccepted")}
					testId="pending-invitation-wait"
				/>
				{view.colleagueOptions.map((option) => {
					const sender = option.displayName ?? option.label;

					return (
						<NoticeChoice
							key={option.id}
							checked={false}
							hint={t("inbox.pendingInvitation.sendFromColleagueHint", { sender, name })}
							label={t("inbox.pendingInvitation.sendFromColleague", { sender })}
							testId={`pending-invitation-colleague-${option.userId}`}
							onSelect={() => onSendFromColleague(option)}
						/>
					);
				})}
			</div>
		</div>
	);
};
